import { useEffect, useState } from "react";
import type { RefObject } from "react";

import { useSimMode } from "./SimModeContext";

/**
 * Settle delay after `hasInput` flips true before measuring. The
 * FeeFooter expansion in `WidgetShell` runs a 400ms `max-height`
 * transition; measuring inside that window returns a mid-animation
 * height, not the steady-state filled height.
 */
const FILLED_SETTLE_MS = 450;

/**
 * In sim mode, capture the widget's *filled-state* card height —
 * the height **right before** the schedule advances to `pending`,
 * with all meta rows + the FeeFooter fully expanded. That measured
 * height becomes the lock applied through `pending → success/error`,
 * so the receipt expand + input collapse happen inside a stable
 * bounding box (matching production's click-time `handleSubmit(offsetHeight)`
 * capture).
 *
 * Mount-time measurement was wrong because at mount `phase === "empty"`
 * — no meta rows, no exchange-rate chip, no FeeFooter expanded — so
 * the card is much shorter than its filled height. We defer to
 * `hasInput`-driven `useEffect` + 450ms settle, which lands inside
 * the ~1500ms `filled` window in every schedule (filled at +1500ms,
 * pending at +3000ms in BUY_SCHEDULE).
 *
 * In production this hook returns `undefined`; widgets should keep
 * using their orchestrator's own `swap.cardHeight` path.
 *
 * @param cardRef the bordered widget card (the same ref already
 *   passed to `<WidgetShell cardRef={...}>`)
 * @param hasInput widget-side "amount filled" flag, typically
 *   `swap.sendAmount !== ""`
 */
export function useSimMeasureCardHeight(cardRef: RefObject<HTMLElement | null>, hasInput: boolean): number | undefined {
    const sim = useSimMode();
    const [height, setHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!sim.enabled) return;
        if (!hasInput) return;
        if (height !== undefined) return;
        const t = window.setTimeout(() => {
            const h = cardRef.current?.offsetHeight;
            if (h && h > 0) setHeight(h);
        }, FILLED_SETTLE_MS);
        return (): void => window.clearTimeout(t);
    }, [sim.enabled, hasInput, height, cardRef]);

    return sim.enabled ? height : undefined;
}
